import { type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  IconArrowUpRight,
  IconBellRinging2,
  IconCategory2,
  IconFileText,
  IconHeartHandshake,
  IconLock,
  IconLogout,
  IconMessage,
  IconRoute,
  IconStethoscope,
  IconUserCircle,
  IconWorld,
} from "@tabler/icons-react";
import { cn } from "../../../../lib/utils";
import { CALENDLY_URL } from "../../../../components/ui/FeedbackCard";
import {
  ACCOUNT_PAGE,
  closeDesktopSettings,
  openDesktopSettings,
  type DesktopSettingsPage,
} from "../../../../domains/app/stores/desktopSettingsStore";
import { usePracticeArm } from "../../../../domains/practice/hooks/usePracticeArm";
import { useLogOutUser } from "../../../../domains/user/hooks/useLogOutUser";
import { useWizardRestart } from "../../../../features/juiscigpt-wizard/useWizardRestart";

/** The nav entries; every other page lights up the entry it opened from. */
export type SettingsNavEntry =
  | "account"
  | "specialty"
  | "language"
  | "notifications"
  | "partnerSharing"
  | "practice"
  | "rooms"
  | "terms"
  | "privacy";

const ROW_CLASS =
  "flex w-full cursor-pointer items-center gap-2.5 rounded-lg border-none px-2.5 py-1.75 text-left text-sm no-underline transition-colors duration-150 [&_svg]:size-4 [&_svg]:shrink-0";

function NavGroupLabel({ children }: { children: string }): React.JSX.Element {
  return (
    <span className="px-2.5 pt-3 pb-1.5 text-xs font-medium text-ds-slate-700">
      {children}
    </span>
  );
}

function NavRow({
  icon,
  label,
  active,
  target,
}: {
  icon: ReactNode;
  label: string;
  active: boolean;
  target: DesktopSettingsPage;
}): React.JSX.Element {
  return (
    <button
      type="button"
      aria-current={active ? "page" : undefined}
      onClick={() => {
        openDesktopSettings(target);
      }}
      className={cn(
        ROW_CLASS,
        active
          ? "bg-ds-slate-100 font-semibold text-ds-slate-950"
          : "bg-transparent font-medium text-ds-slate-700 hover:bg-ds-slate-100/60"
      )}
    >
      {icon}
      <span className="min-w-0 flex-1 truncate">{label}</span>
    </button>
  );
}

/**
 * The modal's left column (Figma "SettingsNav"): the account pages, the legal
 * documents, the feedback link, and Log out at the bottom.
 *
 * The terms and the privacy policy open inside the modal rather than in a new
 * tab, because each carries a Partners tab a consent email links to.
 */
export function SettingsNav({
  active,
}: {
  active: SettingsNavEntry;
}): React.JSX.Element {
  const { t } = useTranslation();
  const logout = useLogOutUser();
  const restartTour = useWizardRestart();
  // The practice arm decides which question shapes this account's feed: its
  // practices, or the topics it follows. The entry waits for the answer.
  const practiceArm = usePracticeArm();

  return (
    <nav className="flex h-full w-56 shrink-0 flex-col gap-0.5 overflow-y-auto border-r border-ds-slate-100 bg-ds-slate-50 px-3 py-5">
      <NavGroupLabel>{t("Account")}</NavGroupLabel>
      <NavRow
        icon={<IconUserCircle stroke={1.5} />}
        label={t("Account")}
        active={active === "account"}
        target={ACCOUNT_PAGE}
      />
      <NavRow
        icon={<IconStethoscope stroke={1.5} />}
        label={t("My main specialty")}
        active={active === "specialty"}
        target={{ page: "specialty" }}
      />
      <NavRow
        icon={<IconWorld stroke={1.5} />}
        label={t("Language")}
        active={active === "language"}
        target={{ page: "language" }}
      />
      <NavRow
        icon={<IconBellRinging2 stroke={1.5} />}
        label={t("Notifications")}
        active={active === "notifications"}
        target={{ page: "notifications" }}
      />
      {/* Figma D3.settings.partner-sharing. */}
      <NavRow
        icon={<IconHeartHandshake stroke={1.5} />}
        label={t("Partner sharing")}
        active={active === "partnerSharing"}
        target={{ page: "partnerSharing" }}
      />
      {practiceArm === "pending" ? null : (
        <NavRow
          icon={<IconStethoscope stroke={1.5} />}
          label={practiceArm === "in" ? t("My practice") : t("My interests")}
          active={active === "practice"}
          target={
            practiceArm === "in" ? { page: "practice" } : { page: "interests" }
          }
        />
      )}
      <NavRow
        icon={<IconCategory2 stroke={1.5} />}
        label={t("My rooms")}
        active={active === "rooms"}
        target={{ page: "rooms", leaving: null }}
      />

      <NavGroupLabel>{t("About")}</NavGroupLabel>
      <NavRow
        icon={<IconFileText stroke={1.5} />}
        label={t("Terms of use")}
        active={active === "terms"}
        target={{ page: "terms", tab: "juisci" }}
      />
      <NavRow
        icon={<IconLock stroke={1.5} />}
        label={t("Privacy policy")}
        active={active === "privacy"}
        target={{ page: "privacy", tab: "juisci" }}
      />

      <NavGroupLabel>{t("Help")}</NavGroupLabel>
      {restartTour === null ? null : (
        <button
          type="button"
          onClick={() => {
            closeDesktopSettings();
            void restartTour();
          }}
          className={cn(
            ROW_CLASS,
            "bg-transparent font-medium text-ds-slate-700 hover:bg-ds-slate-100/60"
          )}
        >
          <IconRoute stroke={1.5} />
          <span className="min-w-0 flex-1 truncate">
            {t("Take the guided tour again")}
          </span>
        </button>
      )}
      <a
        href={CALENDLY_URL}
        target="_blank"
        rel="noreferrer"
        className={cn(
          ROW_CLASS,
          "bg-transparent font-medium text-ds-slate-700 hover:bg-ds-slate-100/60"
        )}
      >
        <IconMessage stroke={1.5} />
        <span className="min-w-0 flex-1 truncate">{t("Give us feedback")}</span>
        <IconArrowUpRight
          stroke={1.5}
          className="size-3.5! text-ds-slate-700"
        />
      </a>

      <button
        type="button"
        onClick={() => {
          closeDesktopSettings();
          void logout();
        }}
        className={cn(
          ROW_CLASS,
          "mt-auto bg-transparent font-medium text-ds-strawberry-800 hover:bg-ds-slate-100/60"
        )}
      >
        <IconLogout stroke={1.5} />
        <span className="min-w-0 flex-1 truncate">{t("Log out")}</span>
      </button>
    </nav>
  );
}
